import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { isBrowser } from 'browser-or-node'
import {
  NetworkError, HttpError, UnsupportedHttpResponseError,
} from '../../errors'
import { HttpRequest, HttpResponse } from '../../interfaces/http'
import { NetworkErrorCode } from '../../interfaces/error/NetworkErrorCode'

// Helper function to convert data prop of request as
// plain object (axios might give it as json string)
const getAsObj = (i: unknown): unknown => {
  if (!i) return undefined
  try {
    return JSON.parse(i as string)
  } catch (e) {
    return i
  }
}

// Extract the request info from AxiosErrors 'config' property
const extractRequest = (axiosCfg: AxiosRequestConfig): HttpRequest => ({
  url: axiosCfg.url,
  method: axiosCfg.method,
  data: getAsObj(axiosCfg.data),
  timeout: axiosCfg.timeout,
  headers: axiosCfg.headers,
})

// Extract the request info from AxiosErrors 'config' property
const extractResponse = (axiosResp: AxiosResponse): HttpResponse => ({
  data: axiosResp.data,
  status: axiosResp.status,
  statusText: axiosResp.statusText,
})

/**
 * Determines the type of NETWORK error by inspecting axios' error code
 * in combination with axios' error message
 *
 * @param axErrCode
 * @param axErrMessage
 */
const mapAxiosNetworkErrorToNetworkErrorCode = (
  axErrCode: string | undefined,
  axErrMessage: string | undefined,
): NetworkErrorCode => {
  if (axErrCode === 'ECONNABORTED') return NetworkErrorCode.NET_REQUEST_TIMEOUT
  if (axErrMessage === 'Network Error' || axErrCode === 'ENOTFOUND') {
    // Note: we can't 100% reliable detect if internet connection is down.
    // https://stackoverflow.com/questions/189430/detect-the-internet-connection-is-offline
    // There is a risk of a false positive (internet connection ok) even though there is no internet
    // access. This happens if is a access to a LAN (router) or virtual network. Then navigator.online
    // will be true regardless of the internet access. In this case the error Code will fall to
    // 'net::no-response' (= no response from server).
    if (isBrowser) { // <- 'navigator' does not exist in node environment (i.e. tests)
      if (!navigator.onLine) return NetworkErrorCode.NET_INTERNET_DISCONNECTED
    }
    return NetworkErrorCode.NET_NO_RESPONSE
  }
  return NetworkErrorCode.NET_UNKNOWN
}

// We can determine if is a 'NETWORK error' if there is no response property on the axiosError
const isNetworkError = (err: AxiosError): boolean => !!err.isAxiosError && !err.response

/**
 * Map an axiosError to an ApiError of the types NetworkError | HttpError
 *
 * @param axiosErr
 */
// eslint-disable-next-line max-len
export const axiosErrorToApiError = (axiosErr: AxiosError): NetworkError | HttpError | UnsupportedHttpResponseError => {
  const httpRequest = extractRequest(axiosErr.config)
  // Network errors
  if (isNetworkError(axiosErr)) {
    const errCode = mapAxiosNetworkErrorToNetworkErrorCode(axiosErr.code, axiosErr.message)
    return new NetworkError({
      code: errCode,
      httpRequest,
      originalError: axiosErr,
    })
  }
  // Errors communicated in the http layer
  if (axiosErr.response) {
    const args = {
      code: axiosErr.response.status,
      message: axiosErr.response.statusText,
      httpRequest,
      httpResponse: extractResponse(axiosErr.response),
      originalError: axiosErr,
    }
    if (args.code >= 400 && args.code <= 599) return new HttpError(args)
    return new UnsupportedHttpResponseError(args)
  }
  throw new Error('unknown axios error')
}
